import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntil } from 'rxjs';
import { SelfSignupService } from '../../services/self-signup.service';
import {
  ConnectionSelfSignup,
  ConnectionSelfSignupStatus,
  OrgType,
  getOrgTypeLabel,
} from '../../models/connection-self-signup';
import { ManageConnectionSignupComponent } from '../manage-connection-signup/manage-connection-signup.component';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { MenuComponentBase } from '../../../../shared/base-classes/menu-component-base';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { FilterComponent } from '../../../../shared/components/filter/filter.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { ConnectionSignupDashboardComponent } from '../connection-signup-dashboard/connection-signup-dashboard.component';
import {
  TableColumn,
  TableComponent,
} from '../../../../shared/components/table/table.component';
import { CommonService } from '../../../../shared/services/common/common.service';

@Component({
  selector: 'app-connection-signup-requests',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    PageSizeComponent,
    PaginationComponent,
    SecondaryPopupComponent,
    ManageConnectionSignupComponent,
    FilterComponent,
    SvgIconComponent,
    ConnectionSignupDashboardComponent,
    TableComponent,
  ],
  templateUrl: './connection-signup-requests.component.html',
  styleUrl: './connection-signup-requests.component.scss',
})
export class ConnectionSignupRequestsComponent
  extends MenuComponentBase
  implements OnInit
{
  selfSignupService = inject(SelfSignupService);
  private commonService = inject(CommonService);

  @ViewChild(ManageConnectionSignupComponent)
  manageSignupComponent?: ManageConnectionSignupComponent;

  loading = true;
  applications: ConnectionSelfSignup[] = [];
  currentPage = 1;
  pageSize = 50;
  totalApplications = 0;
  showDetailsModal = false;
  selectedApplication: ConnectionSelfSignup | null = null;
  searchText = '';
  filterBy = 'all';
  filterByOptions = [
    { option: 'All', value: 'all' },
    { option: 'Pending', value: ConnectionSelfSignupStatus.Pending.toString() },
    // {
    //   option: 'Approved',
    //   value: ConnectionSelfSignupStatus.Approved.toString(),
    // },
    {
      option: 'Rejected',
      value: ConnectionSelfSignupStatus.Rejected.toString(),
    },
    {
      option: 'Approved & Joined',
      value: ConnectionSelfSignupStatus.Joined.toString(),
    },
    {
      option: 'Approved & Not Joined',
      value: ConnectionSelfSignupStatus.NotJoined.toString(),
    },
  ];

  tableColumns: TableColumn[] = [
    { header: 'Organization Name' },
    { header: 'Organization Type' },
    { header: 'Email' },
    { header: 'Website' },
    { header: 'Status' },
    { header: 'Submitted On' },
    { header: '' },
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.loadApplications(this.currentPage, this.pageSize);
  }

  loadApplications(
    pageNumber: number,
    pageSize: number,
    skipLoading = false,
  ): void {
    if (!skipLoading) {
      this.loading = true;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    if (status) {
      this.filterBy = status;
    }

    let statusFilter: ConnectionSelfSignupStatus | undefined;
    let joinedFilter: boolean | undefined;

    if (this.filterBy !== 'all') {
      const filterValue = Number(this.filterBy);
      if (filterValue === ConnectionSelfSignupStatus.Joined) {
        joinedFilter = true;
      } else if (filterValue === ConnectionSelfSignupStatus.NotJoined) {
        joinedFilter = false;
      } else {
        statusFilter = filterValue as ConnectionSelfSignupStatus;
      }
    }

    const searchFilter =
      this.searchText.trim().length > 0 ? this.searchText : undefined;

    this.selfSignupService
      .getAllConnectionSelfSignupApplications(
        pageNumber,
        pageSize,
        statusFilter,
        searchFilter,
        joinedFilter,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.applications = response.results;
          this.totalApplications = response.count;
          this.loading = false;
          const applicationId = urlParams.get('application');
          if (applicationId) {
            const application = this.applications.find(
              (app) => app.id.toString() === applicationId,
            );
            if (application) {
              this.viewApplicationDetails(application);
            }
          }
        },
        error: (error) => {
          console.error('Error loading applications:', error);
          this.loading = false;
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadApplications(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadApplications(this.currentPage, this.pageSize);
    }
  }

  viewApplicationDetails(application: ConnectionSelfSignup): void {
    this.selectedApplication = application;
    const url = new URL(window.location.href);
    url.searchParams.set('application', application.id.toString());
    window.history.pushState({}, '', url.toString());
    this.showDetailsModal = true;
  }

  onDetailsModalClosed(reload: boolean): void {
    this.showDetailsModal = false;
    this.selectedApplication = null;
    const url = new URL(window.location.href);
    url.searchParams.delete('application');
    window.history.pushState({}, '', url.toString());
    if (reload) {
      this.loadApplications(this.currentPage, this.pageSize, true);
    }
  }

  getStatusLabel(application: ConnectionSelfSignup): string {
    if (!application.status) return 'Pending';

    // Check if approved and has joined information
    if (application.status === ConnectionSelfSignupStatus.Approved) {
      if (application.joined === true) {
        return 'Joined';
      } else if (application.joined === false) {
        return 'Not Joined';
      }
      return 'Approved';
    }

    switch (application.status) {
      case ConnectionSelfSignupStatus.Pending:
        return 'Pending';
      case ConnectionSelfSignupStatus.Rejected:
        return 'Rejected';
      case ConnectionSelfSignupStatus.Joined:
        return 'Joined';
      case ConnectionSelfSignupStatus.NotJoined:
        return 'Not Joined';
      default:
        return 'Unknown';
    }
  }

  getStatusClass(application: ConnectionSelfSignup): string {
    if (!application.status) return 'pending';

    // Check if approved and has joined information
    if (application.status === ConnectionSelfSignupStatus.Approved) {
      if (application.joined === true) {
        return 'joined';
      } else if (application.joined === false) {
        return 'not-joined';
      }
      return 'approved';
    }

    switch (application.status) {
      case ConnectionSelfSignupStatus.Pending:
        return 'pending';
      case ConnectionSelfSignupStatus.Rejected:
        return 'rejected';
      case ConnectionSelfSignupStatus.Joined:
        return 'joined';
      case ConnectionSelfSignupStatus.NotJoined:
        return 'not-joined';
      default:
        return 'unknown';
    }
  }

  onFilterByChange(value: string): void {
    this.filterBy = value;
    this.currentPage = 1;
    const url = new URL(window.location.href);
    if (value === 'all') {
      url.searchParams.delete('status');
    } else {
      url.searchParams.set('status', value);
    }
    window.history.pushState({}, '', url.toString());
    this.loadApplications(this.currentPage, this.pageSize, true);
  }

  onSearchQueryChange(query: string): void {
    this.searchText = query;
    this.currentPage = 1;
    this.loadApplications(this.currentPage, this.pageSize, true);
  }

  getConnectionSelfSignupStatus(): typeof ConnectionSelfSignupStatus {
    return ConnectionSelfSignupStatus;
  }

  getOrgTypeLabel(orgType: OrgType): string {
    return getOrgTypeLabel(orgType);
  }

  ensureHttpProtocol(url: string): string {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      return 'https://' + url;
    }
    return url;
  }

  onApproveAction(): void {
    this.manageSignupComponent?.approveApplication();
  }

  onRejectAction(): void {
    this.manageSignupComponent?.showRejectPopup();
  }
}
